<template>
    <div class="wa-contact-list" :class="{ 'wa-contact-list--mobile-full': mobileMode }">
        <!-- Header -->
        <div class="wa-contact-list__header">
            <div class="wa-contact-list__header-top">
                <div class="wa-contact-list__channel-label" v-if="selectedChannel">
                    <span class="wa-contact-list__channel-name">{{ selectedChannel.name || $t("whatsappUi.defaultChannelName") }}</span>
                    <span class="wa-contact-list__channel-type">{{ getChannelTypeLabel(selectedChannel.type) }}</span>
                </div>
                <span v-else class="wa-contact-list__header-title">{{ $t("whatsappUi.defaultChannelName") }}</span>
                <div class="wa-contact-list__header-actions">
                    <button
                        class="wa-contact-list__icon-btn"
                        v-tippy="{ content: $t('whatsappUi.newChatTooltip') }"
                        @click="showNewConversationModal = true"
                    >
                        <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
                            <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.17L4 17.17V4h16v12zM7 9h10M7 13h7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                        </svg>
                    </button>
                    <button
                        v-if="!hideInbox"
                        class="wa-contact-list__icon-btn"
                        v-tippy="{ content: $t('whatsappUi.filterTooltip') }"
                        @click="toggleFilterPanel"
                    >
                        <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
                            <path d="M3 4.5h18M7.5 10.5h9M10.5 16.5h3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                        </svg>
                    </button>
                </div>
            </div>

            <!-- Search -->
            <div class="wa-contact-list__search">
                <div class="wa-contact-list__search-icon">
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
                        <path d="M11 3a8 8 0 100 16 8 8 0 000-16zM21 21l-4.35-4.35" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                    </svg>
                </div>
                <input
                    ref="searchInput"
                    v-model="searchQuery"
                    class="wa-contact-list__search-input"
                    :placeholder="$t('whatsappUi.searchPlaceholder')"
                    @input="onSearch"
                    @focus="searchFocused = true"
                    @blur="searchFocused = false"
                />
                <button
                    v-if="searchQuery"
                    class="wa-contact-list__search-clear"
                    @click="clearSearch"
                >
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none">
                        <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                    </svg>
                </button>
            </div>

            <!-- Filter chips: accordion — max 3 rows, rest collapses -->
            <div class="wa-contact-list__filters" v-if="showFilterPanel && !hideInbox">
                <div
                    class="wa-contact-list__chips-wrap"
                    :class="{ 'wa-contact-list__chips-wrap--expanded': filtersExpanded }"
                    ref="chipsWrap"
                >
                    <span
                        class="wa-contact-list__chip"
                        :class="{ 'wa-contact-list__chip--active': activeChip === 'all' }"
                        @click="setChip('all')"
                    >{{ $t("whatsappUi.chipAll") }}</span>
                    <span
                        class="wa-contact-list__chip"
                        :class="{ 'wa-contact-list__chip--active': activeChip === 'unread' }"
                        @click="setChip('unread')"
                    >{{ $t("whatsappUi.chipUnread") }}</span>
                    <span
                        class="wa-contact-list__chip"
                        :class="{ 'wa-contact-list__chip--active': activeChip === 'open' }"
                        @click="setChip('open')"
                    >{{ $t("whatsappUi.chipOpen") }}</span>
                    <span
                        class="wa-contact-list__chip"
                        :class="{ 'wa-contact-list__chip--active': activeChip === 'closed' }"
                        @click="setChip('closed')"
                    >{{ $t("whatsappUi.chipClosed") }}</span>
                    <span
                        v-for="f in customFilters"
                        :key="f._id || f.id"
                        class="wa-contact-list__chip wa-contact-list__chip--custom"
                        :class="{ 'wa-contact-list__chip--active': activeChip === (f._id || f.id) }"
                        @click="setFilterChip(f)"
                    >{{ f.name }}</span>
                </div>
                <button
                    v-if="showFilterToggle"
                    class="wa-contact-list__filter-toggle"
                    @click="filtersExpanded = !filtersExpanded"
                >
                    {{ filtersExpanded ? $t("whatsappUi.filterShowLess") : $t("whatsappUi.filterShowMore") }}
                </button>
            </div>
        </div>

        <!-- New conversation modal -->
        <Modal2
            v-if="showNewConversationModal"
            :show-footer="false"
            :title="$t('whatsappUi.startChatModalTitle')"
            @cancel="showNewConversationModal = false; newConversationPhone = ''"
        >
            <WhatsappChatStarter
                channel-override
                phone-number-override
                :default-phone-number="newConversationPhone"
                @close="showNewConversationModal = false; newConversationPhone = ''"
            />
        </Modal2>

        <!-- Contact list -->
        <div
            class="wa-contact-list__body"
            ref="contactListBody"
            @scroll="onScroll"
        >
            <template v-if="contacts.length > 0">
                <WAContactListItem
                    v-for="contact in contacts"
                    :key="contact._id"
                    :contact="contact"
                    :selected-contact="selectedContact"
                    @click="onContactClick"
                />
                <div class="wa-contact-list__loading-more" v-if="loadingMore">
                    <span class="wa-contact-list__dot-loader">
                        <span /><span /><span />
                    </span>
                </div>
            </template>

            <div v-else-if="loading" class="wa-contact-list__loading">
                <div v-for="i in 8" :key="i" class="wa-contact-list__skeleton">
                    <div class="wa-contact-list__skeleton-avatar" />
                    <div class="wa-contact-list__skeleton-content">
                        <div class="wa-contact-list__skeleton-name" />
                        <div class="wa-contact-list__skeleton-msg" />
                    </div>
                </div>
            </div>

            <div v-else class="wa-contact-list__empty">
                <svg viewBox="0 0 24 24" width="40" height="40" fill="none">
                    <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" fill="#ccc"/>
                </svg>
                <p>Konuşma bulunamadı</p>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import WAContactListItem from "./WAContactListItem.vue";
import Modal2 from "../../../../components/common/Modal2.vue";
import WhatsappChatStarter from "../../../../views/Pages/Integrations/meta-leads/WhatsappChatStarter.vue";
import _ from "lodash";

export default {
    name: "WAContactList",
    components: { WAContactListItem, Modal2, WhatsappChatStarter },
    props: {
        selectedContact: {
            type: Object,
            default: null,
        },
        selectedChannel: {
            type: Object,
            default: null,
        },
        mobileMode: {
            type: Boolean,
            default: false,
        },
        hideInbox: {
            type: Boolean,
            default: false,
        },
    },
    data: () => ({
        searchQuery: "",
        searchFocused: false,
        showFilterPanel: true,
        activeChip: "all",
        activeFilterObject: null,
        loadingMore: false,
        loading: false,
        filtersExpanded: false,
        showFilterToggle: false,
        showNewConversationModal: false,
        newConversationPhone: "",
        debouncedSearch: null,
    }),
    computed: {
        ...mapState({
            contacts: (state) => state.contact.contacts,
            filters: (state) => state.contact.filters,
            paginationCursor: (state) => state.contact.paginationCursor,
        }),
        customFilters() {
            return this.filters.filter((f) => f.type === "custom" || f.type === "system");
        },
    },
    watch: {
        selectedChannel(newVal) {
            this.loadContacts(true);
        },
    },
    created() {
        this.debouncedSearch = _.debounce(this.loadContacts, 400);
    },
    mounted() {
        this.getContactListFilters();
        this.$nextTick(this.checkFilterOverflow);
        window.addEventListener("message", this.onExtensionMessage);
    },
    beforeDestroy() {
        window.removeEventListener("message", this.onExtensionMessage);
    },
    methods: {
        ...mapActions({
            getFilteredContacts: "contact/getFilteredContacts",
            setSelectedContact: "contact/setSelectedContact",
            getContactListFilters: "contact/getContactListFilters",
        }),
        onExtensionMessage(event) {
            if (!event.data || event.data.type !== "supsis:openPhone") return;
            const phone = event.data.phone;
            if (!phone) return;
            this.newConversationPhone = phone;
            this.showNewConversationModal = true;
        },
        getChannelTypeLabel(type) {
            var labels = {
                "whatsapp-business-api": this.$t("whatsappUi.channelTypeBusinessApi"),
                "whatsapp-business-api-supsis": this.$t("whatsappUi.channelTypeBusinessApiSupsis"),
                "whatsapp-unipile": this.$t("whatsappUi.channelTypeUnipile"),
                "whatsapp-v2": this.$t("whatsappUi.channelTypeQr"),
                "whatsapp-qr": this.$t("whatsappUi.channelTypeQr"),
            };
            return labels[type] || this.$t("whatsappUi.defaultChannelName");
        },
        isPhoneNumber(query) {
            // Rakam, +, boşluk, - içeriyorsa ve en az 5 karakter ise telefon numarası say
            return /^[+\d\s\-()]{5,}$/.test(query.trim());
        },
        buildFilters() {
            const filters = [{ field: "platform", operator: "equals", value: ["whatsapp"] }];
            if (this.selectedChannel) {
                filters.push({ field: "channel", operator: "equals", value: [this.selectedChannel._id] });
            }
            return filters;
        },
        buildPayload(opts) {
            const opts_ = opts || {};
            const payload = {
                source: "WAContactList.loadContacts",
                filters: this.buildFilters(),
                limit: 30,
                appendContacts: opts_.append || false,
            };

            const q = this.searchQuery && this.searchQuery.trim();
            if (q) {
                payload.search = {
                    field: this.isPhoneNumber(q) ? "phone" : "all",
                    query: q,
                };
            }

            if (this.activeChip === "unread") {
                payload.metadata = { unreadFilter: "unread" };
            } else if (this.activeChip === "open") {
                payload.state = "open";
            } else if (this.activeChip === "closed") {
                payload.state = "closed";
            }

            if (this.activeFilterObject) {
                payload.inbox = {
                    type: this.activeFilterObject.type || "custom",
                    id: this.activeFilterObject._id || this.activeFilterObject.id,
                };
                payload.filters = this.buildFilters();
            }

            if (opts_.nextToken) {
                payload.nextToken = opts_.nextToken;
            }

            return payload;
        },
        checkFilterOverflow() {
            this.$nextTick(() => {
                const wrap = this.$refs.chipsWrap;
                if (!wrap) return;
                // 3 satır height = chip height (28px) * 3 + gap (6px) * 2 = ~96px
                const THREE_ROWS_HEIGHT = 96;
                this.showFilterToggle = wrap.scrollHeight > THREE_ROWS_HEIGHT;
            });
        },
        async loadContacts(reset) {
            this.loading = true;
            try {
                await this.getFilteredContacts(this.buildPayload({ append: false }));
            } finally {
                this.loading = false;
            }
        },
        onSearch() {
            this.debouncedSearch(true);
        },
        clearSearch() {
            this.searchQuery = "";
            this.loadContacts(true);
        },
        toggleFilterPanel() {
            this.showFilterPanel = !this.showFilterPanel;
        },
        setChip(chip) {
            this.activeChip = chip;
            this.activeFilterObject = null;
            this.loadContacts(true);
        },
        setFilterChip(filter) {
            this.activeChip = filter._id || filter.id;
            this.activeFilterObject = filter;
            this.loadContacts(true);
        },
        onContactClick(contact) {
            this.setSelectedContact(contact);
            this.$emit("contact-selected", contact);
        },
        async onScroll(e) {
            const el = e.target;
            const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
            if (!nearBottom || this.loadingMore) return;

            const nextToken = this.paginationCursor && this.paginationCursor.nextToken;
            if (!nextToken) return;

            this.loadingMore = true;
            try {
                await this.getFilteredContacts(this.buildPayload({ append: true, nextToken }));
            } finally {
                this.loadingMore = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.wa-contact-list {
    width: 380px;
    min-width: 300px;
    max-width: 420px;
    background: #fff;
    display: flex;
    flex-direction: column;
    border-right: 1px solid #e9edef;
    flex-shrink: 0;

    // mobile modda tam genişlik
    &--mobile-full {
        width: 100% !important;
        min-width: 0 !important;
        max-width: none !important;
        border-right: none;
        flex: 1;
        min-height: 0; // flex child'ın içeriğine göre büyümesini engeller, scroll çalışır
        overflow: hidden;
    }

    &__header {
        background: #fff;
        border-bottom: 1px solid #e9edef;
        flex-shrink: 0;
    }

    &__header-top {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 10px 16px 6px;
        height: 60px;
    }

    &__header-title {
        font-size: 20px;
        font-weight: 600;
        color: #111b21;
    }

    &__channel-label {
        display: flex;
        flex-direction: column;
    }

    &__channel-name {
        font-size: 16px;
        font-weight: 600;
        color: #111b21;
        line-height: 1.2;
    }

    &__channel-type {
        font-size: 11px;
        color: #128C7E;
        font-weight: 500;
    }

    &__header-actions {
        display: flex;
        gap: 4px;
    }

    &__icon-btn {
        width: 36px;
        height: 36px;
        border-radius: 50%;
        border: none;
        background: transparent;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #54656f;
        transition: background-color 0.15s;
        outline: none;

        &:hover {
            background: #f5f6f6;
            color: #111b21;
        }
    }

    &__search {
        margin: 4px 12px 8px;
        background: #f0f2f5;
        border-radius: 10px;
        display: flex;
        align-items: center;
        padding: 0 10px;
        gap: 8px;
        height: 38px;
    }

    &__search-icon {
        color: #54656f;
        display: flex;
        align-items: center;
        flex-shrink: 0;
    }

    &__search-input {
        flex: 1;
        border: none;
        background: transparent;
        outline: none;
        font-size: 15px;
        color: #111b21;
        min-width: 0;

        &::placeholder {
            color: #8696a0;
        }
    }

    &__search-clear {
        border: none;
        background: transparent;
        cursor: pointer;
        color: #54656f;
        display: flex;
        align-items: center;
        padding: 0;
        outline: none;

        &:hover {
            color: #111b21;
        }
    }

    &__filters {
        padding: 4px 12px 6px;
    }

    &__chips-wrap {
        display: flex;
        flex-wrap: wrap;
        gap: 5px;
        overflow: hidden;
        max-height: 96px; // max 3 rows (chip ~28px + gap)
        transition: max-height 0.25s ease;

        &--expanded {
            max-height: 400px;
        }
    }

    &__filter-toggle {
        background: none;
        border: none;
        color: #128C7E;
        font-size: 11px;
        font-weight: 500;
        cursor: pointer;
        padding: 3px 0 0;
        outline: none;

        &:hover {
            text-decoration: underline;
        }
    }

    &__chip {
        padding: 4px 14px;
        border-radius: 20px;
        font-size: 13px;
        font-weight: 500;
        background: #f0f2f5;
        color: #667781;
        cursor: pointer;
        transition: all 0.15s;
        user-select: none;
        white-space: nowrap;

        &:hover {
            background: #e9edef;
            color: #111b21;
        }

        &--active {
            background: #d9fdd3;
            color: #128C7E;

            &:hover {
                background: #c8f5c2;
            }
        }

        &--custom {
            background: #f0f2f5;
        }
    }

    &__body {
        flex: 1;
        overflow-y: auto;
        overflow-x: hidden;

        &::-webkit-scrollbar {
            width: 5px;
        }

        &::-webkit-scrollbar-thumb {
            background: rgba(0,0,0,0.15);
            border-radius: 3px;
        }
    }

    &__loading {
        padding: 8px 0;
    }

    &__skeleton {
        display: flex;
        align-items: center;
        padding: 12px 16px;
        gap: 12px;
        border-bottom: 1px solid #f0f2f5;
    }

    &__skeleton-avatar {
        width: 49px;
        height: 49px;
        border-radius: 50%;
        background: linear-gradient(90deg, #f0f2f5 25%, #e9edef 50%, #f0f2f5 75%);
        background-size: 200% 100%;
        animation: shimmer 1.4s infinite;
        flex-shrink: 0;
    }

    &__skeleton-content {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    &__skeleton-name {
        height: 14px;
        width: 60%;
        border-radius: 4px;
        background: linear-gradient(90deg, #f0f2f5 25%, #e9edef 50%, #f0f2f5 75%);
        background-size: 200% 100%;
        animation: shimmer 1.4s infinite;
    }

    &__skeleton-msg {
        height: 12px;
        width: 80%;
        border-radius: 4px;
        background: linear-gradient(90deg, #f0f2f5 25%, #e9edef 50%, #f0f2f5 75%);
        background-size: 200% 100%;
        animation: shimmer 1.4s infinite 0.2s;
    }

    &__loading-more {
        display: flex;
        justify-content: center;
        padding: 12px;
    }

    &__dot-loader {
        display: flex;
        gap: 5px;
        align-items: center;

        span {
            width: 7px;
            height: 7px;
            border-radius: 50%;
            background: #25D366;
            display: block;
            animation: dotBounce 1.2s ease-in-out infinite;

            &:nth-child(2) { animation-delay: 0.2s; }
            &:nth-child(3) { animation-delay: 0.4s; }
        }
    }

    &__empty {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 60px 20px;
        color: #8696a0;

        p {
            margin-top: 12px;
            font-size: 14px;
        }
    }
}

@keyframes shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
}

@keyframes dotBounce {
    0%, 100% { transform: translateY(0); opacity: 1; }
    50% { transform: translateY(-5px); opacity: 0.6; }
}
</style>
